import {
  Ban,
  CheckCircle2,
  Edit3,
  Eye,
  Loader2,
} from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import { RestaurantPurchaseOrderStatus } from '../../../../types/restaurant/RestaurantPurchaseOrderTypes';
import { formatMoney } from '../../../../utils/decimalValue';
import { PurchaseOrderItem } from '../types';
import { isRestaurantPurchasePaymentComplete } from '../restaurantPurchasePayment';


interface PurchaseTableProps {
  purchases: PurchaseOrderItem[];
  actingId: number | null;
  onView: (purchase: PurchaseOrderItem) => void;
  onEdit: (purchase: PurchaseOrderItem) => void;
  onConfirm: (purchase: PurchaseOrderItem) => void;
  onCancel: (purchase: PurchaseOrderItem) => void;
}

const statusInfo: Record<
  RestaurantPurchaseOrderStatus,
  { label: string; classes: string }
> = {
  DRAFT: {
    label: 'Borrador',
    classes:
      'bg-warning/10 text-warning dark:bg-warning/15',
  },
  CONFIRMED: {
    label: 'Confirmada',
    classes:
      'bg-success/10 text-success dark:bg-success/15',
  },
  CANCELLED: {
    label: 'Cancelada',
    classes:
      'bg-danger/10 text-danger dark:bg-danger/15',
  },
};

const formatPurchaseDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString('es-SV');

const PurchaseTable = ({
  purchases,
  actingId,
  onView,
  onEdit,
  onConfirm,
  onCancel,
}: PurchaseTableProps) => {
  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="w-full min-w-[1240px] text-left text-sm">
        <thead className="bg-gray-2 text-xs uppercase tracking-wide text-bodydark2 dark:bg-meta-4/60">
          <tr>
            <th className="px-4 py-3 font-medium">Compra</th>
            <th className="px-4 py-3 font-medium">Proveedor</th>
            <th className="px-4 py-3 font-medium">Fecha</th>
            <th className="px-4 py-3 font-medium">Estado</th>
            <th className="px-4 py-3 text-center font-medium">
              Detalles
            </th>
            <th className="px-4 py-3 text-right font-medium">
              Total
            </th>
            <th className="px-4 py-3 text-right font-medium">
              Acciones
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-stroke dark:divide-strokedark">
          {purchases.map((purchase) => {
            const acting = actingId === purchase.id;
            const canConfirm = isRestaurantPurchasePaymentComplete(
              purchase.purchaseDate,
              purchase.paymentCondition
            );

            return (
              <tr
                key={purchase.id}
                className="transition-colors hover:bg-gray-2/40 dark:hover:bg-meta-4/20"
              >
                <td className="px-4 py-4 font-semibold text-primary">
                  {purchase.purchaseNumber}
                </td>

                <td className="px-4 py-4 font-medium text-black dark:text-white">
                  {purchase.supplierName}
                </td>

                <td className="px-4 py-4 text-body dark:text-bodydark">
                  {formatPurchaseDate(purchase.purchaseDate)}
                </td>

                <td className="px-4 py-4">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                      statusInfo[purchase.status].classes
                    }`}
                  >
                    {statusInfo[purchase.status].label}
                  </span>
                </td>

                <td className="px-4 py-4 text-center font-medium text-black dark:text-white">
                  {purchase.detailCount}
                </td>

                <td className="px-4 py-4 text-right font-semibold text-black dark:text-white">
                  {formatMoney(purchase.total)}
                </td>

                <td className="px-4 py-4">
                  <div className="flex flex-wrap justify-end gap-2">
                    <ActionButton
                      label="Ver"
                      icon={<Eye className="h-4 w-4" />}
                      variant="secondary"
                      size="sm"
                      fullWidth={false}
                      disabled={acting}
                      onClick={() => onView(purchase)}
                    />

                    {purchase.status === 'DRAFT' && (
                      <>
                        <ActionButton
                          label="Editar"
                          icon={<Edit3 className="h-4 w-4" />}
                          variant="info"
                          size="sm"
                          fullWidth={false}
                          disabled={acting}
                          onClick={() => onEdit(purchase)}
                        />

                        <ActionButton
                          label={acting ? 'Procesando' : 'Confirmar'}
                          icon={
                            acting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4" />
                            )
                          }
                          variant="success"
                          size="sm"
                          fullWidth={false}
                          disabled={acting || !canConfirm}
                          onClick={() => onConfirm(purchase)}
                        />

                        <ActionButton
                          label="Cancelar"
                          icon={<Ban className="h-4 w-4" />}
                          variant="danger"
                          size="sm"
                          fullWidth={false}
                          disabled={acting}
                          onClick={() => onCancel(purchase)}
                        />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PurchaseTable;
